import type { EntityModifyParameters, ExtendedEntityParameters } from 'src/components/EntityDetail';
import type { ValidationResult } from 'types';
import {
    AddUserDocument,
    DeleteUserBadgeDocument,
    UpdateUserDocument,
    UpsertUserBadgeDocument,
    UserFragment,
    UserMaintenanceFragment,
} from 'types/db';
import { isAdmin, userValidations } from './user.validations';
import { gqlClient } from 'src/util/gql-client';
import { apiTrpcHttpClient } from 'src/util/api-trpc';
import { getListChanges } from 'src/util/composables/watch-list-changes';

export type UserEntity = Pick<UserFragment, 'username' | 'description' | 'firstName' | 'lastName' | 'enabled' | 'id'> & {
    userContexts: { id: string, context_id: string }[];
    userRoles: { id: string, context_id: string, role_id: string }[];
    localAuth?: { id: string, email: string, password: string, currentPassword: string };
    userAccessTokens?: { id: string, acls: string[], name: string, created_at?: string }[];
    badge?: { badge_id: string } | null;
    createdAt?: string;
    createdBy: Pick<UserFragment, 'firstName' | 'lastName' | 'username'> | null;
    updatedAt?: string;
    updatedBy: Pick<UserFragment, 'firstName' | 'lastName' | 'username'> | null;
};

export function userFragmentToEntityType (userFragment: UserMaintenanceFragment): UserEntity {
    return {
        description: userFragment.description,
        enabled: userFragment.enabled,
        firstName: userFragment.firstName,
        id: userFragment.id,
        lastName: userFragment.lastName,
        userContexts: userFragment.user_contexts.map(uc => ({
            id: uc.id,
            context_id: uc.context_id,
        })),
        userRoles: userFragment.user_roles.map(ur => ({
            id: ur.id,
            context_id: ur.context_id,
            role_id: ur.role_id,
        })),
        username: userFragment.username || '',
        createdBy: userFragment.createdBy || null,
        createdAt: userFragment.createdAt,
        updatedBy: userFragment.updatedBy || null,
        updatedAt: userFragment.updatedAt || undefined,
        localAuth: {
            id: userFragment.localAuth?.id || '',
            email: userFragment.localAuth?.email || '',
            password: '',
            currentPassword: '',
        },
        userAccessTokens: userFragment.userAccessTokens?.map(uat => ({
            id: uat.id || '',
            acls: Array.isArray(uat.acls) ? uat.acls : [],
            name: uat.name || '',
            created_at: uat.created_at || '',
        })) || [],
        badge: userFragment.badge ? { badge_id: userFragment.badge.badge_id } : null,
    };
}

export const createUserEntity = async (payload: EntityModifyParameters<'user'>): Promise<ValidationResult> => {
    const { entity: user } = payload;
    const result = userValidations(user);
    if (result.status !== 'success') {
        return result;
    }
    try {
        await gqlClient.request({
            document: AddUserDocument,
            variables: {
                user: {
                    id: user.id,
                    username: user.username,
                    first_name: user.firstName,
                    last_name: user.lastName,
                    description: user.description,
                    enabled: user.enabled,
                    user_contexts: {
                        data: user.userContexts.map(l => {
                            return isAdmin()
                                ? {
                                    id: l.id,
                                    context_id: l.context_id,
                                }
                                : {
                                    id: l.id,
                                };
                        }),
                    },
                    user_roles: {
                        data: user.userRoles.map(l => {
                            return isAdmin()
                                ? {
                                    id: l.id,
                                    context_id: l.context_id,
                                    role_id: l.role_id,
                                }
                                : {
                                    id: l.id,
                                    role_id: l.role_id,
                                };
                        }),
                    },
                },
            },
        });

        if (`${user.localAuth?.email || ''}`.length > 0) {
            await apiTrpcHttpClient.auth.provider.local.create.mutate({
                userId: user.id,
                email: `${user.localAuth?.email || ''}`.toLowerCase().trim(),
                password: user.localAuth?.password || '',
            });
        }

        return {
            status: 'success',
        };

    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving User: "${JSON.stringify(err)}"`,
        };
    }
};

export const saveUserEntity = async (payload: ExtendedEntityParameters<'user'>): Promise<ValidationResult> => {
    const {
        entity: user,
        oldEntity: originalUser,
    } = payload;
    const result = userValidations(user);
    if (result.status !== 'success') {
        return result;
    }
    
    const userContextPlc = getListChanges(user.userContexts, originalUser?.userContexts || [], { deep: true, format: 'plc' });
    const userRolePlc = getListChanges(user.userRoles, originalUser?.userRoles || [], { deep: true, format: 'plc' });
    const userAccessTokens = getListChanges(user.userAccessTokens || [], originalUser?.userAccessTokens || [], { deep: true, format: 'plc' });
    try {
        await gqlClient.request({
            document: UpdateUserDocument,
            variables: {
                isAdmin: isAdmin(),
                userId: user.id,
                user: {
                    description: user.description,
                    enabled: user.enabled,
                    first_name: user.firstName,
                    last_name: user.lastName,
                    username: user.username,
                },
                insertUserContexts: userContextPlc.inserts.map(uc => ({
                    id: uc.id,
                    context_id: uc.context_id,
                    user_id: user.id,
                })),
                deleteUserContextsBoolExp: {
                    id: { _in: userContextPlc.deletes.map(uc => uc.id) },
                },
                insertUserRoles: userRolePlc.inserts.map(i => {
                    return isAdmin()
                        ? {
                            id: i.id,
                            user_id: user.id,
                            role_id: i.role_id,
                            context_id: i.context_id,
                        }
                        : {
                            id: i.id,
                            user_id: user.id,
                            role_id: i.role_id,
                        };
                }),
                deleteUserRoleBoolExp: {
                    id: { _in: userRolePlc.deletes.map(ur => ur.id) },
                },
                deleteUserAccessTokenKeysBoolExp: {
                    id: { _in: userAccessTokens.deletes.map(ka => ka.id) },
                },
            },
        });

        // Updating a user's password is done async in the user settings page
        if (!originalUser?.localAuth?.id && `${user.localAuth?.email || ''}`.length > 0) {
            await apiTrpcHttpClient.auth.provider.local.create.mutate({
                userId: user.id,
                email: `${user.localAuth?.email || ''}`.toLowerCase().trim(),
                password: user.localAuth?.password || '',
            });
        }

        if (user.badge?.badge_id !== originalUser?.badge?.badge_id) {
            if (user.badge?.badge_id) {
                await gqlClient.request({
                    document: UpsertUserBadgeDocument,
                    variables: { userId: user.id, badgeId: user.badge.badge_id },
                });
            } else if (originalUser?.badge?.badge_id) {
                await gqlClient.request({
                    document: DeleteUserBadgeDocument,
                    variables: { userId: user.id },
                });
            }
        }

        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving User: "${JSON.stringify(err)}"`,
        };
    }
};
